"use client";

import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { rememberLastLoginEmail } from "@/lib/auth/lastLoginEmail";
import { rememberTrimbleSignIn } from "@/lib/auth/trimbleSignInHistory";
import { reportAuthFailure, setAuthSubject } from "@/lib/authBackend/trimble/telemetry";
import { linkTrimbleAccount } from "../client";
import {
  decodeIdTokenClaims,
  type LoginRedirectOptions,
  TIDClient,
  type TIDConfig,
  type TokenResponse,
  type UserInfo,
} from "./client";
import type { SignupPrefill } from "./signupPrefill";
import type { AuthStatePayload } from "./state";

// Re-checking on every tab focus (not a fixed timer) targets the actual gap: an invite
// or Trimble-side acceptance can land while this tab sits open and untouched, with no
// natural re-authentication. Throttled so rapid tab-switching doesn't hammer the endpoint.
const MIN_RELINK_INTERVAL_MS = 60_000;

type AuthContextValue = {
  client: TIDClient;
  isLoading: boolean;
  isAuthenticated: boolean;
  authFlow: AuthStatePayload["flow"] | null;
  user: UserInfo | null;
  error: Error | null;
  loginWithRedirect: (state?: AuthStatePayload, options?: LoginRedirectOptions) => Promise<void>;
  signupWithRedirect: (state: AuthStatePayload, prefill?: SignupPrefill) => Promise<void>;
  logout: (state?: AuthStatePayload | null) => Promise<void>;
  getTokens: () => Promise<TokenResponse>;
  getAccessTokenSilently: () => Promise<string>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export type TIDProviderProps = {
  /** Pass an existing TIDClient instance (singleton pattern). Takes precedence over config. */
  client?: TIDClient;
  /** Config to create a new TIDClient from. Required when client is not provided. */
  config?: TIDConfig;
  children: ReactNode;
};

export function TIDProvider({ client: clientProp, config, children }: TIDProviderProps) {
  const [client] = useState<TIDClient>(() => {
    if (clientProp) return clientProp;
    if (!config) throw new Error("TIDProvider requires either client or config prop");
    return new TIDClient(config);
  });

  // Start in a loading state when an E2E session rehydrate is pending. AuthGate is a child
  // of this provider, so its effect runs before this provider's rehydrate effect; seeding
  // isLoading=true makes the gate wait for the (synchronous) rehydration instead of firing
  // a redirect on the first tick. Rendered output is unchanged (the gate already shows the
  // loading screen whenever unauthenticated), so there's no hydration mismatch.
  const [isLoading, setIsLoading] = useState(() => client.hasValidStoredSession() && !client.isAuthenticated());
  const [isAuthenticated, setIsAuthenticated] = useState(client.isAuthenticated());
  const [authFlow, setAuthFlow] = useState(client.getAuthFlow());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const lastRelinkAtRef = useRef(0);

  useEffect(() => {
    return client.subscribe(() => {
      const auth = client.isAuthenticated();
      setIsAuthenticated(auth);
      setAuthFlow(client.getAuthFlow());
      if (!auth) {
        setUser(null);
        // The subject outlives React state (it is module-level), so without this a second
        // user signing in on the same tab and failing before their token decodes would be
        // reported to Rollbar under the previous user's Trimble sub.
        setAuthSubject(null);
      }
    });
  }, [client]);

  // E2E only: a context restored from saved storageState boots unauthenticated (the TID
  // session is in-memory). Replay the persisted tokens to authenticate on cold load — no
  // redirect — before AuthGate falls back to the SSO round-trip. Inert in prod.
  useEffect(() => {
    if (!client.hasValidStoredSession() || client.isAuthenticated()) {
      // Always unblock loading — if the session expired between the useState
      // initializer and this effect, isLoading could otherwise stay stuck at true.
      setIsLoading(false);
      return;
    }
    client.rehydrateFromStorage();
    setIsLoading(false);
  }, [client]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    client
      .getTokens()
      .then((tokens) => {
        if (cancelled) return;
        const claims = decodeIdTokenClaims(tokens.id_token);
        setUser(claims);
        setAuthSubject(claims?.sub);
        rememberLastLoginEmail(claims?.email);
        rememberTrimbleSignIn();
        // E2E only: persist here (off the auth-critical path) using the tokens this effect
        // already fetched, so a cold load can rehydrate without a redirect. Inert in prod.
        client.persistSession(tokens);
      })
      .catch((err) => {
        // Nothing renders this context's `error`, so without a report a session that goes
        // bad immediately after authenticating leaves no trace at all.
        reportAuthFailure("token_refresh", err, { trigger: "post_authentication" });
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [client, isAuthenticated]);

  // Re-links (idempotent — see linkTrimbleAccount) whenever this tab regains focus,
  // so an invite or Trimble-side acceptance that happened while this tab sat open and
  // authenticated gets picked up without waiting for a hard refresh.
  useEffect(() => {
    if (!isAuthenticated || authFlow === "trial-signup") return;

    const maybeRelink = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRelinkAtRef.current < MIN_RELINK_INTERVAL_MS) return;
      lastRelinkAtRef.current = now;
      void linkTrimbleAccount().catch((err) => {
        reportAuthFailure("relink_on_focus", err);
      });
    };

    document.addEventListener("visibilitychange", maybeRelink);
    return () => {
      document.removeEventListener("visibilitychange", maybeRelink);
    };
  }, [authFlow, isAuthenticated]);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setIsLoading(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const loginWithRedirect = useCallback(
    async (state?: AuthStatePayload, options?: LoginRedirectOptions) => {
      setIsLoading(true);
      setError(null);
      try {
        await client.loginWithRedirect(state, options);
      } catch (err) {
        setIsLoading(false);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [client],
  );

  const logout = useCallback(
    async (state?: AuthStatePayload | null) => {
      setIsLoading(true);
      try {
        await client.logout(state);
      } catch (err) {
        setIsLoading(false);
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        throw e;
      }
    },
    [client],
  );

  const signupWithRedirect = useCallback(
    async (state: AuthStatePayload, prefill?: SignupPrefill) => {
      setIsLoading(true);
      setError(null);
      try {
        await client.signupWithRedirect(state, prefill);
      } catch (err) {
        setIsLoading(false);
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    },
    [client],
  );

  const getTokens = useCallback(() => client.getTokens(), [client]);
  const getAccessTokenSilently = useCallback(() => client.getAccessTokenSilently(), [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      client,
      isLoading,
      isAuthenticated,
      authFlow,
      user,
      error,
      loginWithRedirect,
      signupWithRedirect,
      logout,
      getTokens,
      getAccessTokenSilently,
    }),
    [
      client,
      isLoading,
      isAuthenticated,
      authFlow,
      user,
      error,
      loginWithRedirect,
      signupWithRedirect,
      logout,
      getTokens,
      getAccessTokenSilently,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <TIDProvider>");
  return ctx;
}

export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
